import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  TextField,
  Checkbox,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import {
  getReleaseCalendarEvents,
  getReleaseSignOff,
  saveReleaseSignOff,
  saveReleaseMicroservices,
  deleteReleaseMicroservice
} from '../../services/apiService';
import { isAuthenticated } from '../../services/authService';
import { formatDate } from '../../utils/formatters';

/**
 * Default microservice list seeded into the artifacts table when a release has
 * none saved yet. EDIT THIS LIST to match the services your teams report on
 * dev-freeze day — rows can also be added/removed per release in the UI.
 */
const DEFAULT_MICROSERVICES = [
  'Isheet',
  'Isheet Connector',
  'Isheet UI Modernization',
  'Isheet Data Importer',
  'Metadata',
  'System Cache',
  'Search',
  'Unified Search',
  'UDB (Unified DashboardBuilder)',
  'DashboardBuilder',
  'Workflow',
  'AIhub',
  'AIEngine',
  'Smart Form Builder',
  'Third Party',
  'Task UI Modernization'
];

/**
 * Release Sign Off tab — auto-derived "control tower" for a release.
 *
 * Reuses data already loaded by the parent Releases view (bugs + PRs) and pulls
 * milestone dates (dev freeze / release / PM sign-off) from the Release Calendar.
 * Everything above the Go / No-Go panel is computed; no manual data entry.
 *
 * Go / No-Go sign-off is persisted to a dedicated ReleaseSignOff collection
 * (GET/PATCH /api/fix-versions/:version/sign-off). Each approval records who signed
 * off, when, and a snapshot of the readiness metrics at that moment. Reading is
 * public; toggling requires authentication (any logged-in user).
 *
 * @param {Object} props
 * @param {string} props.fixVersion - Selected release version (e.g. "5.11.00")
 * @param {Object} props.releaseDetails - FixVersion detail (incl. releaseDate)
 * @param {Array} props.productionBugs - Production bugs for the release
 * @param {Array} props.internalBugs - Internal bugs for the release
 * @param {Array} props.pullRequests - PRs associated with the release
 * @param {Object} props.prsByWorkItem - PRs grouped by work item id
 * @returns {JSX.Element}
 */
const ReleaseSignOffTab = ({
  fixVersion = '',
  releaseDetails = null,
  productionBugs = [],
  internalBugs = [],
  pullRequests = [],
  prsByWorkItem = {}
}) => {
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  // Persisted sign-off record (server is source of truth). `record` holds the full
  // document; `signOff` booleans are derived from it for the readiness calc below.
  const [record, setRecord] = useState(null);
  const [savingKey, setSavingKey] = useState(null); // which control is mid-save
  const [saveError, setSaveError] = useState('');
  const canEdit = isAuthenticated();

  // ---- Microservice artifacts table (RC tags teams provide on dev-freeze day) ----
  // `msRows` is the merged view: the seeded default list overlaid with saved values.
  const [msRows, setMsRows] = useState([]);
  const [savingMs, setSavingMs] = useState(false);
  const [msError, setMsError] = useState('');

  const signOff = useMemo(() => ({
    dev: record?.signOffs?.dev?.approved || false,
    qa: record?.signOffs?.qa?.approved || false,
    product: record?.signOffs?.product?.approved || false
  }), [record]);

  // ---- Load persisted sign-off for this release ----
  useEffect(() => {
    if (!fixVersion) return;
    let cancelled = false;
    getReleaseSignOff(fixVersion)
      .then((data) => { if (!cancelled) setRecord(data); })
      .catch((err) => { if (!cancelled) console.error('Failed to load release sign-off:', err); });
    return () => { cancelled = true; };
  }, [fixVersion]);

  // ---- Fetch release-calendar events to derive milestone dates ----
  useEffect(() => {
    if (!fixVersion) return;
    const year = new Date().getFullYear();
    setLoadingCalendar(true);
    Promise.all([
      getReleaseCalendarEvents(year).catch(() => []),
      getReleaseCalendarEvents(year + 1).catch(() => [])
    ])
      .then(([cur, next]) => {
        const all = [...(Array.isArray(cur) ? cur : []), ...(Array.isArray(next) ? next : [])];
        setCalendarEvents(all);
      })
      .finally(() => setLoadingCalendar(false));
  }, [fixVersion]);

  // ---- Build the microservice table: seed defaults, overlay saved values ----
  // Always shows the default list (so teams have a checklist), with saved RC tags /
  // flags / audit layered on top, plus any custom services added for this release.
  useEffect(() => {
    const saved = (record && Array.isArray(record.microservices)) ? record.microservices : [];
    const savedByName = new Map(saved.map((m) => [m.name, m]));
    const merged = DEFAULT_MICROSERVICES.map((name) => {
      const s = savedByName.get(name);
      savedByName.delete(name);
      return {
        name,
        rcTag: s?.rcTag || '',
        needsUpgrade: s?.needsUpgrade || false,
        applied: s?.applied || false,
        updatedByName: s?.updatedByName || '',
        updatedAt: s?.updatedAt || null,
        isDefault: true,
        dirty: false
      };
    });
    // Any saved custom services not in the default list keep showing.
    savedByName.forEach((s) => merged.push({
      name: s.name,
      rcTag: s.rcTag || '',
      needsUpgrade: s.needsUpgrade || false,
      applied: s.applied || false,
      updatedByName: s.updatedByName || '',
      updatedAt: s.updatedAt || null,
      isDefault: false,
      dirty: false
    }));
    setMsRows(merged);
  }, [record]);

  const hasDirtyMs = msRows.some((r) => r.dirty && r.name.trim());

  const handleMsChange = (idx, field, value) => {
    setMsRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value, dirty: true } : r)));
  };

  const handleAddMicroservice = () => {
    setMsRows((rows) => [...rows, {
      name: '', rcTag: '', needsUpgrade: false, applied: false,
      updatedByName: '', updatedAt: null, isDefault: false, isNew: true, dirty: true
    }]);
  };

  const handleSaveMicroservices = async () => {
    if (!canEdit) return;
    const toSave = msRows.filter((r) => r.dirty && r.name.trim());
    if (!toSave.length) return;
    setSavingMs(true);
    setMsError('');
    try {
      const updated = await saveReleaseMicroservices(fixVersion, toSave.map((r) => ({
        name: r.name.trim(), rcTag: r.rcTag, needsUpgrade: r.needsUpgrade, applied: r.applied
      })));
      setRecord(updated); // re-merges via effect, clearing dirty flags
    } catch (err) {
      setMsError(err.message || 'Failed to save microservices');
    } finally {
      setSavingMs(false);
    }
  };

  const handleDeleteMicroservice = async (idx) => {
    if (!canEdit) return;
    const row = msRows[idx];
    if (!row) return;
    // Unsaved (new / empty) row → just drop it locally.
    if (row.isNew || !row.name.trim()) {
      setMsRows((rows) => rows.filter((_, i) => i !== idx));
      return;
    }
    setSavingMs(true);
    setMsError('');
    try {
      const updated = await deleteReleaseMicroservice(fixVersion, row.name);
      setRecord(updated);
    } catch (err) {
      setMsError(err.message || 'Failed to delete microservice');
    } finally {
      setSavingMs(false);
    }
  };

  // Normalise a version string to comparable numeric parts: "5.11.00" -> "5.11.0"
  const normVersion = (v) =>
    String(v || '')
      .trim()
      .split('.')
      .map((p) => String(parseInt(p, 10) || 0))
      .join('.');

  // ---- Milestone dates from the calendar (matched to this release) ----
  const milestones = useMemo(() => {
    const target = normVersion(fixVersion);
    const eventsForRelease = calendarEvents.filter(
      (e) => normVersion(e.release) === target
    );
    const dateOf = (...types) => {
      const match = eventsForRelease
        .filter((e) => types.includes(e.eventType))
        .map((e) => new Date(e.date))
        .sort((a, b) => a - b)[0];
      return match || null;
    };
    return {
      devFreeze: dateOf('feature_dev_freeze', 'maintenance_dev_freeze'),
      pmSignoff: dateOf('pm_signoff'),
      release:
        dateOf('feature_release', 'maintenance_release') ||
        (releaseDetails?.releaseDate ? new Date(releaseDetails.releaseDate) : null)
    };
  }, [calendarEvents, fixVersion, releaseDetails]);

  // ---- Bug analysis ----
  const allBugs = useMemo(
    () => [...(productionBugs || []), ...(internalBugs || [])],
    [productionBugs, internalBugs]
  );

  const isOpen = (bug) => {
    const s = String(bug.state || '').toLowerCase();
    return !['closed', 'done', 'resolved', 'removed'].includes(s);
  };

  const bugStats = useMemo(() => {
    const freeze = milestones.devFreeze;
    const byPriority = { P1: 0, P2: 0, P3: 0, P4: 0, other: 0 };
    const afterFreeze = { P1: 0, P2: 0, P3: 0, P4: 0, other: 0 };
    let openP1 = 0;
    let openTotal = 0;

    allBugs.forEach((bug) => {
      const p = ['P1', 'P2', 'P3', 'P4'].includes(bug.priority) ? bug.priority : 'other';
      const open = isOpen(bug);
      if (open) {
        byPriority[p] += 1;
        openTotal += 1;
        if (p === 'P1') openP1 += 1;
      }
      if (freeze && bug.createdDate && new Date(bug.createdDate) > freeze) {
        afterFreeze[p] += 1;
      }
    });

    return { byPriority, afterFreeze, openP1, openTotal };
  }, [allBugs, milestones.devFreeze]);

  // ---- PR analysis ----
  const prStats = useMemo(() => {
    const freeze = milestones.devFreeze;

    // Build a set of PR keys (number|repository) that ARE linked to a work item
    const linkedKeys = new Set();
    Object.values(prsByWorkItem || {}).forEach((list) => {
      (list || []).forEach((pr) => linkedKeys.add(`${pr.number}|${pr.repository}`));
    });

    let afterFreeze = 0;
    let unlinked = 0;
    let lastMerge = null;

    (pullRequests || []).forEach((pr) => {
      const mergedAt = pr.mergedAt || pr.closedAt || pr.mergedDate || null;
      const isMerged = pr.merged || /merge|complete|closed/i.test(pr.status || pr.state || '');
      if (mergedAt) {
        const d = new Date(mergedAt);
        if (!lastMerge || d > lastMerge.date) lastMerge = { date: d, repo: pr.repository, number: pr.number };
        if (freeze && isMerged && d > freeze) afterFreeze += 1;
      }
      if (!linkedKeys.has(`${pr.number}|${pr.repository}`)) unlinked += 1;
    });

    return { afterFreeze, unlinked, lastMerge, total: (pullRequests || []).length };
  }, [pullRequests, prsByWorkItem, milestones.devFreeze]);

  // ---- Microservice upgrade progress (only services flagged "Needs upgrade") ----
  const msStats = useMemo(() => {
    const needing = msRows.filter((r) => r.needsUpgrade);
    const applied = needing.filter((r) => r.applied).length;
    return { total: needing.length, applied, pending: needing.length - applied };
  }, [msRows]);

  // ---- Days to release ----
  const daysToRelease = useMemo(() => {
    if (!milestones.release) return null;
    const ms = milestones.release.getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  }, [milestones.release]);

  // ---- Overall readiness status (auto recommendation) ----
  const readiness = useMemo(() => {
    const reasons = [];
    let level = 'ready'; // ready | risk | blocked

    if (bugStats.openP1 > 0) {
      level = 'blocked';
      reasons.push({ tone: 'red', text: `${bugStats.openP1} open P1 bug${bugStats.openP1 > 1 ? 's' : ''}` });
    }
    if (bugStats.byPriority.P2 > 0) {
      if (level !== 'blocked') level = 'risk';
      reasons.push({ tone: 'amber', text: `${bugStats.byPriority.P2} open P2 bug${bugStats.byPriority.P2 > 1 ? 's' : ''}` });
    }
    if (prStats.afterFreeze > 0) {
      if (level !== 'blocked') level = 'risk';
      reasons.push({ tone: 'amber', text: `${prStats.afterFreeze} PR${prStats.afterFreeze > 1 ? 's' : ''} merged after freeze` });
    }
    if (prStats.unlinked > 0) {
      if (level !== 'blocked') level = 'risk';
      reasons.push({ tone: 'amber', text: `${prStats.unlinked} PR${prStats.unlinked > 1 ? 's' : ''} with no linked work item` });
    }
    const signedCount = Object.values(signOff).filter(Boolean).length;
    if (signedCount < 3) {
      if (level === 'ready') level = 'risk';
      reasons.push({ tone: 'amber', text: `${3 - signedCount} sign-off${3 - signedCount > 1 ? 's' : ''} pending` });
    } else {
      reasons.push({ tone: 'green', text: 'All sign-offs received' });
    }
    if (level === 'ready') reasons.unshift({ tone: 'green', text: 'No blocking defects' });

    return { level, reasons, signedCount };
  }, [bugStats, prStats, signOff]);

  // Snapshot of the auto-derived metrics AT the moment of a decision — persisted
  // alongside the sign-off so "approved" is meaningful even after the data shifts.
  const buildSnapshot = () => ({
    openP1: bugStats.byPriority.P1,
    openTotal: bugStats.openTotal,
    prsAfterFreeze: prStats.afterFreeze,
    unlinkedPRs: prStats.unlinked,
    recommendation: readiness.level
  });

  // ---- Persist a single role toggle ----
  const handleToggleRole = async (roleKey, approved) => {
    if (!canEdit) return;
    setSavingKey(roleKey);
    setSaveError('');
    try {
      const updated = await saveReleaseSignOff(fixVersion, { role: roleKey, approved, metricsSnapshot: buildSnapshot() });
      setRecord(updated);
    } catch (err) {
      setSaveError(err.message || 'Failed to save sign-off');
    } finally {
      setSavingKey(null);
    }
  };

  // ---- Persist the final Go / No-Go decision ----
  const handleFinalDecision = async (decision) => {
    if (!canEdit) return;
    setSavingKey('final');
    setSaveError('');
    try {
      const updated = await saveReleaseSignOff(fixVersion, { finalDecision: decision, metricsSnapshot: buildSnapshot() });
      setRecord(updated);
    } catch (err) {
      setSaveError(err.message || 'Failed to save decision');
    } finally {
      setSavingKey(null);
    }
  };

  if (!fixVersion) {
    return (
      <Alert severity="info">
        Select a release in the <strong>Release Content</strong> tab to view its sign-off readiness.
      </Alert>
    );
  }

  // ---- Style helpers ----
  const STATUS = {
    ready: { label: '🟢 Ready', color: '#2e7d32', bg: 'linear-gradient(135deg,#2e7d32,#43a047)' },
    risk: { label: '🟡 At Risk', color: '#b8860b', bg: 'linear-gradient(135deg,#b8860b,#caa221)' },
    blocked: { label: '🔴 Blocked', color: '#c62828', bg: 'linear-gradient(135deg,#c62828,#e53935)' }
  };
  const status = STATUS[readiness.level];
  const toneColor = { red: { bg: '#fde3e3', fg: '#c62828' }, amber: { bg: '#fff4e0', fg: '#c77700' }, green: { bg: '#e8f3e8', fg: '#2e7d32' } };

  const kpiCard = (value, label, color) => (
    <Grid item xs={6} md={2}>
      <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: color.bg, borderRadius: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: color.fg }}>{value}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>{label}</Typography>
      </Paper>
    </Grid>
  );

  return (
    <Box>
      {/* Persistence / auth notice */}
      <Alert severity={canEdit ? 'info' : 'warning'} sx={{ mb: 2 }}>
        Readiness metrics are auto-derived from this release&apos;s bugs, PRs and the release calendar.
        Go / No-Go sign-off is <strong>saved</strong> — each approval records who signed off, when, and the
        metrics at that moment.
        {!canEdit && ' Sign in to record or change a sign-off; the saved decision is shown below read-only.'}
      </Alert>
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      {/* ---- Health banner ---- */}
      <Paper elevation={2} sx={{ display: 'flex', flexWrap: 'wrap', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ flex: '0 0 280px', background: status.bg, color: '#fff', p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>{status.label}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
            Release {fixVersion}
            {daysToRelease != null && ` · ${daysToRelease >= 0 ? `${daysToRelease} day${daysToRelease !== 1 ? 's' : ''} to go` : `${Math.abs(daysToRelease)} day(s) overdue`}`}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 3, minWidth: 280 }}>
          <Grid container spacing={1}>
            {readiness.reasons.map((r, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Chip
                  label={r.text}
                  size="small"
                  sx={{ bgcolor: toneColor[r.tone].bg, color: toneColor[r.tone].fg, fontWeight: 600 }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>

      {/* ---- KPI cards ---- */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Readiness at a glance</Typography>
        <Grid container spacing={2}>
          {kpiCard(bugStats.byPriority.P1, 'Open P1 bugs', bugStats.byPriority.P1 > 0 ? toneColor.red : toneColor.green)}
          {kpiCard(prStats.afterFreeze, 'PRs after freeze', prStats.afterFreeze > 0 ? toneColor.amber : toneColor.green)}
          {kpiCard(prStats.unlinked, 'Unlinked PRs', prStats.unlinked > 0 ? toneColor.amber : toneColor.green)}
          {kpiCard(bugStats.openTotal, 'Open bugs (total)', bugStats.openTotal > 0 ? toneColor.amber : toneColor.green)}
          {kpiCard(`${readiness.signedCount}/3`, 'Sign-offs received', readiness.signedCount === 3 ? toneColor.green : { bg: '#e3f0fb', fg: '#1f6fb2' })}
          {kpiCard(
            `${msStats.applied}/${msStats.total}`,
            'MS upgrades applied',
            msStats.total === 0
              ? { bg: '#e3f0fb', fg: '#1f6fb2' }
              : msStats.pending === 0 ? toneColor.green : toneColor.amber
          )}
        </Grid>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 1 }}>
        {/* ---- Defects & risk ---- */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>🐞 Defects &amp; risk</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Severity</TableCell>
                    <TableCell align="center">Open</TableCell>
                    <TableCell align="center">Opened after freeze</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['P1', 'P2', 'P3', 'P4'].map((p) => (
                    <TableRow key={p}>
                      <TableCell><Chip label={p} size="small" sx={{ bgcolor: p === 'P1' ? '#fde3e3' : p === 'P2' ? '#fff4e0' : '#e8f3e8', fontWeight: 700 }} /></TableCell>
                      <TableCell align="center">{bugStats.byPriority[p]}</TableCell>
                      <TableCell align="center">{bugStats.afterFreeze[p]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary">
              Auto-derived from this release&apos;s production + internal bugs and the dev-freeze date.
            </Typography>
          </Paper>
        </Grid>

        {/* ---- PR & code-freeze discipline ---- */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>🔁 PR &amp; code-freeze discipline</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Check</TableCell>
                    <TableCell align="center">Count</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>PRs merged after dev freeze</TableCell>
                    <TableCell align="center">{prStats.afterFreeze}</TableCell>
                    <TableCell align="center">{renderFlag(prStats.afterFreeze === 0)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>PRs with no linked work item</TableCell>
                    <TableCell align="center">{prStats.unlinked}</TableCell>
                    <TableCell align="center">{renderFlag(prStats.unlinked === 0)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Total PRs in release</TableCell>
                    <TableCell align="center">{prStats.total}</TableCell>
                    <TableCell align="center">—</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Last merge</TableCell>
                    <TableCell align="center" colSpan={2}>
                      {prStats.lastMerge
                        ? `${formatDate(prStats.lastMerge.date)} · ${prStats.lastMerge.repo || 'repo'}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary">
              Computed from this release&apos;s pull requests + work-item links.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ---- Timeline ---- */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>🕒 Release timeline</Typography>
        {loadingCalendar ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={18} /><Typography variant="body2" color="text.secondary">Loading milestones…</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {renderMilestone('Dev freeze', milestones.devFreeze)}
            {renderMilestone('PM sign-off', milestones.pmSignoff)}
            {renderMilestone('Release', milestones.release)}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">
          Milestones pulled from the Release Calendar. {(!milestones.devFreeze && !milestones.pmSignoff) && 'No calendar events matched this release — add them in the Release Calendar to enable freeze/timeline checks.'}
        </Typography>
      </Paper>

      {/* ---- Microservice artifacts (dev-freeze RC tags) ---- */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>📦 Microservice artifacts (dev-freeze)</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {savingMs && <CircularProgress size={18} />}
            <Button size="small" startIcon={<AddIcon />} onClick={handleAddMicroservice} disabled={!canEdit || savingMs}>
              Add service
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveMicroservices}
              disabled={!canEdit || savingMs || !hasDirtyMs}
            >
              Save changes
            </Button>
          </Box>
        </Box>
        {msError && <Alert severity="error" sx={{ my: 1 }}>{msError}</Alert>}
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 180 }}>Service</TableCell>
                <TableCell sx={{ minWidth: 160 }}>RC tag / artifact</TableCell>
                <TableCell align="center">Needs upgrade</TableCell>
                <TableCell align="center">Applied</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {msRows.map((row, idx) => (
                <TableRow key={row.isDefault ? row.name : `custom-${idx}`}>
                  <TableCell>
                    {row.isDefault ? (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.name}</Typography>
                    ) : (
                      <TextField
                        variant="standard"
                        size="small"
                        placeholder="Service name"
                        value={row.name}
                        disabled={!canEdit || savingMs}
                        onChange={(e) => handleMsChange(idx, 'name', e.target.value)}
                        fullWidth
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      size="small"
                      placeholder="e.g. 2.1.0-RC.1"
                      value={row.rcTag}
                      disabled={!canEdit || savingMs}
                      onChange={(e) => handleMsChange(idx, 'rcTag', e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={row.needsUpgrade}
                      disabled={!canEdit || savingMs}
                      onChange={(e) => handleMsChange(idx, 'needsUpgrade', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      color="success"
                      checked={row.applied}
                      disabled={!canEdit || savingMs}
                      onChange={(e) => handleMsChange(idx, 'applied', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    {row.updatedAt ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.updatedByName || 'someone'} · {formatDate(new Date(row.updatedAt))}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {!row.isDefault && (
                      <Tooltip title="Remove service">
                        <span>
                          <IconButton size="small" onClick={() => handleDeleteMicroservice(idx)} disabled={!canEdit || savingMs}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Teams enter each service&apos;s RC tag on dev-freeze day; tick <strong>Needs upgrade</strong> if it changed this release.
          Tick <strong>Applied</strong> once the upgrade has been done. Every edit records who updated it and when.
          {!canEdit && ' Sign in to edit — this table is read-only for you.'}
        </Typography>
      </Paper>

      {/* ---- Go / No-Go ---- */}
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>🚦 Go / No-Go decision</Typography>
        <Alert severity={status.label.includes('Ready') ? 'success' : status.label.includes('Blocked') ? 'error' : 'warning'} sx={{ mb: 2 }}>
          System recommendation: <strong>{status.label}</strong>
        </Alert>
        <Grid container spacing={2}>
          {[
            { key: 'dev', label: 'Dev sign-off' },
            { key: 'qa', label: 'QA sign-off' },
            { key: 'product', label: 'Product sign-off' }
          ].map((role) => {
            const info = record?.signOffs?.[role.key];
            return (
              <Grid item xs={12} sm={4} key={role.key}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">{role.label}</Typography>
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Switch
                        checked={signOff[role.key]}
                        disabled={!canEdit || savingKey === role.key}
                        onChange={(e) => handleToggleRole(role.key, e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      savingKey === role.key
                        ? 'Saving…'
                        : signOff[role.key] ? 'Approved' : 'Pending'
                    }
                  />
                  {info?.approved && info?.userName && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      by {info.userName}
                      {info.decidedAt ? ` · ${formatDate(new Date(info.decidedAt))}` : ''}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
          <Button
            variant={record?.finalDecision === 'ready' ? 'contained' : 'outlined'}
            color="success"
            disabled={!canEdit || readiness.signedCount < 3 || savingKey === 'final'}
            onClick={() => handleFinalDecision('ready')}
          >
            ✅ Mark Ready to Release
          </Button>
          <Button
            variant={record?.finalDecision === 'hold' ? 'contained' : 'outlined'}
            color="error"
            disabled={!canEdit || savingKey === 'final'}
            onClick={() => handleFinalDecision('hold')}
          >
            ⛔ Hold Release
          </Button>
          {savingKey === 'final' && <CircularProgress size={18} />}
        </Box>
        {record?.finalDecision && (
          <Typography variant="body2" sx={{ display: 'block', mt: 1.5, fontWeight: 600, color: record.finalDecision === 'ready' ? '#2e7d32' : '#c62828' }}>
            {record.finalDecision === 'ready' ? '✅ Marked Ready to Release' : '⛔ Release on Hold'}
            {record.finalDecisionByName ? ` — by ${record.finalDecisionByName}` : ''}
            {record.finalDecidedAt ? ` · ${formatDate(new Date(record.finalDecidedAt))}` : ''}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {canEdit
            ? 'Sign-offs and the final decision are saved with the approver and a snapshot of the readiness metrics at the time.'
            : 'Sign in to record or change sign-offs. The saved decision above is read-only.'}
        </Typography>
      </Paper>
    </Box>
  );
};

// Green tick / red cross flag for boolean checks
const renderFlag = (ok) =>
  ok ? (
    <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32', verticalAlign: 'middle' }} />
  ) : (
    <CancelIcon fontSize="small" sx={{ color: '#c62828', verticalAlign: 'middle' }} />
  );

// A timeline milestone pill: done (past), now (overdue/pending), upcoming
const renderMilestone = (label, date) => {
  const now = Date.now();
  let state = 'pending';
  if (date) state = date.getTime() < now ? 'done' : 'upcoming';
  const colors = {
    done: { border: '#2e7d32', icon: <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32' }} /> },
    upcoming: { border: '#9e9e9e', icon: <HourglassEmptyIcon fontSize="small" sx={{ color: '#9e9e9e' }} /> },
    pending: { border: '#bdbdbd', icon: <HourglassEmptyIcon fontSize="small" sx={{ color: '#bdbdbd' }} /> }
  }[state];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${colors.border}`, borderRadius: 2, px: 2, py: 1, minWidth: 170 }}>
      {colors.icon}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{date ? formatDate(date) : 'not set'}</Typography>
      </Box>
    </Box>
  );
};

export default ReleaseSignOffTab;
